import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTasks } from '../context/TaskContext';
import { useNotes } from '../context/NoteContext';
import { useDashboard } from '../context/DashboardContext';
import apiClient from '../services/apiClient';
import {
  DashboardLayout,
  DashboardContainer,
  DashboardHeader,
  StatsGrid,
  ChartsGrid,
  StatCard,
  ModernPieChart,
  ModernLineChart,
  ModernBarChart,
  ActivityHeatmap,
  PeakPerformanceHours,
  FocusTimeAnalysis,
  WeeklyGoalsProgress,
  RecentActivity,
  AIInsights,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Heading2,
  Body,
  Small,
  cn
} from '../design-system';

const DashboardScreen = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { notes, fetchNotes, loading: notesLoading } = useNotes();
  const { 
    dashboardData, 
    loading: dashboardLoading, 
    fetchDashboardData, 
    fetchTasksCompletedPerDay, 
    fetchTaskDurationByCategory, 
    fetchCompletionStatusOverTime, 
    fetchTasksByHour, 
    fetchCompletionHeatmap 
  } = useDashboard();
  
  const [tasksCompletedData, setTasksCompletedData] = useState([]);
  const [taskDurationData, setTaskDurationData] = useState([]);
  const [completionStatusData, setCompletionStatusData] = useState([]);
  const [hourlyTaskData, setHourlyTaskData] = useState([]);
  const [heatmapData, setHeatmapData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  // Navigation items matching the image
  const navigationItems = [
    { id: 'analytics', label: 'Analytics', icon: 'fa-solid fa-chart-line' },
    { id: 'meetings', label: 'Meetings', icon: 'fa-solid fa-users' },
    { id: 'notes', label: 'Notes', icon: 'fa-solid fa-sticky-note' },
    { id: 'tasks', label: 'Tasks', icon: 'fa-solid fa-list-check' },
    { id: 'categories', label: 'Categories', icon: 'fa-solid fa-layer-group' },
  ];

  const handleNavigation = (item) => {
    switch (item.id) {
      case 'analytics':
        navigate('/dashboard');
        break;
      case 'meetings':
        navigate('/calendar');
        break;
      case 'notes':
        navigate('/notes');
        break;
      case 'tasks':
        navigate('/tasks');
        break;
      case 'categories':
        // If categories page doesn't exist, show a message or navigate to dashboard
        navigate('/tasks'); // For now, redirect to tasks
        break;
      default:
        navigate('/dashboard');
    }
  };

  const getDateRange = (period = '2weeks') => {
    const endDate = new Date();
    let startDate = new Date();

    switch (period) {
      case 'week':
        startDate.setDate(endDate.getDate() - endDate.getDay());
        break;
      case '2weeks':
        startDate.setDate(endDate.getDate() - 14);
        break;
      case 'month':
        startDate.setDate(endDate.getDate() - 30);
        break;
      default:
        startDate.setDate(endDate.getDate() - 14);
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  const fetchAnalyticsData = async () => {
    setAnalyticsLoading(true);
    try {
      const { startDate, endDate } = getDateRange();
      const [
        completedData, 
        durationData, 
        statusOverTimeData, 
        hourlyData, 
        heatmapAnalytics
      ] = await Promise.all([
        fetchTasksCompletedPerDay(startDate, endDate),
        fetchTaskDurationByCategory(startDate, endDate),
        fetchCompletionStatusOverTime(startDate, endDate),
        fetchTasksByHour(startDate, endDate),
        fetchCompletionHeatmap(startDate, endDate)
      ]);
      
      setTasksCompletedData(completedData);
      setTaskDurationData(durationData);
      setCompletionStatusData(statusOverTimeData);
      setHourlyTaskData(hourlyData);
      setHeatmapData(heatmapAnalytics);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };
  
  useEffect(() => {
    if (isAuthenticated) {
      console.log('User is authenticated, fetching dashboard data...');
      fetchDashboardData();
      fetchNotes();
      fetchAnalyticsData();
    } else {
      console.log('User is not authenticated');
    }
  }, [isAuthenticated]);
  
  // Add debugging useEffect to monitor dashboardData changes
  useEffect(() => {
    console.log('Dashboard data changed:', {
      dashboardData,
      loading: dashboardLoading,
      hasData: !!dashboardData,
      categoryDistribution: dashboardData?.categoryDistribution,
      weekStats: dashboardData?.weekStats
    });
  }, [dashboardData, dashboardLoading]);
  
  // Chart data preparation functions using real API data
  const getProductivityTrendsData = () => {
    if (!tasksCompletedData || tasksCompletedData.length === 0) {
      // Fallback data if no API data available
      return {
        labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
        datasets: [
          {
            label: 'Completed Tasks',
            data: [12, 19, 15, 17, 25, 22, 20],
            borderColor: '#10B981',
            backgroundColor: '#10B98120',
            borderWidth: 3,
            fill: false,
            tension: 0.4,
            pointBackgroundColor: '#10B981',
            pointBorderColor: '#FFFFFF',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6
          },
          {
            label: 'Pending Tasks',
            data: [8, 12, 10, 14, 18, 15, 12],
            borderColor: '#F59E0B',
            backgroundColor: '#F59E0B20',
            borderWidth: 3,
            fill: false,
            tension: 0.4,
            pointBackgroundColor: '#F59E0B',
            pointBorderColor: '#FFFFFF',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6
          }
        ]
      };
    }

    const labels = tasksCompletedData.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    });

    const completedData = tasksCompletedData.map(item => item.completed_count);
    const pendingData = tasksCompletedData.map(item => item.pending_count || 0); // Use pending_count from API

    return {
      labels,
      datasets: [
        {
          label: 'Completed Tasks',
          data: completedData,
          borderColor: '#10B981',
          backgroundColor: '#10B98120',
          borderWidth: 3,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: '#10B981',
          pointBorderColor: '#FFFFFF',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6
        },
        {
          label: 'Pending Tasks',
          data: pendingData,
          borderColor: '#F59E0B',
          backgroundColor: '#F59E0B20',
          borderWidth: 3,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: '#F59E0B',
          pointBorderColor: '#FFFFFF',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    };
  };

  const getTaskCategoriesData = () => {
    // Check if dashboardData and categoryDistribution exist
    if (!dashboardData?.categoryDistribution || dashboardData.categoryDistribution.length === 0) {
      console.log('Using fallback data for pie chart - no dashboard data available');
      // Fallback data if no API data available
      return {
        labels: ['Work', 'Personal', 'Shopping', 'Health', 'Learning'],
        datasets: [
          {
            data: [25, 15, 12, 10, 8],
            backgroundColor: [
              '#6366F1', // Work - blue
              '#EC4899', // Personal - pink
              '#8B5CF6', // Shopping - purple
              '#EF4444', // Health - red
              '#10B981', // Learning - green
            ],
            borderColor: '#FFFFFF',
            borderWidth: 2,
          }
        ]
      };
    }

    console.log('Dashboard data available:', dashboardData);
    const labels = dashboardData.categoryDistribution.map(cat => cat.name);
    const data = dashboardData.categoryDistribution.map(cat => cat.taskCount); // Use taskCount instead of count
    const colors = ['#6366F1', '#EC4899', '#8B5CF6', '#EF4444', '#10B981', '#F59E0B', '#06B6D4', '#84CC16'];

    console.log('Using API data for pie chart:', { labels, data });

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors.slice(0, data.length),
          borderColor: '#FFFFFF',
          borderWidth: 2,
        }
      ]
    };
  };

  const getTaskActivityByHourData = () => {
    // If no data, return fallback data
    if (!hourlyTaskData || hourlyTaskData.length === 0) {
      // Fallback data showing activity by hour (sorted by hour)
      const fallbackData = [
        { hour: 7, task_count: 1 },
        { hour: 8, task_count: 1 },
        { hour: 9, task_count: 8 },
        { hour: 10, task_count: 4 },
        { hour: 11, task_count: 6 },
        { hour: 12, task_count: 3 },
        { hour: 13, task_count: 5 },
        { hour: 14, task_count: 7 },
        { hour: 15, task_count: 7 },
        { hour: 16, task_count: 3 },
        { hour: 17, task_count: 2 },
        { hour: 18, task_count: 1 },
        { hour: 19, task_count: 3 },
        { hour: 20, task_count: 2 },
        { hour: 21, task_count: 2 },
        { hour: 22, task_count: 1 }
      ];
      
      const labels = fallbackData.map(item => {
        const hour = parseInt(item.hour);
        if (hour === 0) return '12 AM';
        if (hour < 12) return `${hour} AM`;
        if (hour === 12) return '12 PM';
        return `${hour - 12} PM`;
      });

      const data = fallbackData.map(item => item.task_count);
      const backgroundColors = data.map(value => {
        if (value >= 7) return '#EF4444'; // High activity - red
        if (value >= 4) return '#F59E0B'; // Medium activity - yellow
        return '#10B981'; // Low activity - green
      });

      return {
        labels,
        datasets: [
          {
            label: 'Tasks Completed',
            data,
            backgroundColor: backgroundColors,
            borderColor: '#FFFFFF',
            borderWidth: 1,
            borderRadius: 6,
            borderSkipped: false,
          }
        ]
      };
    }

    // Sort hourly data by hour in ascending order (0-23)
    const sortedHourlyData = [...hourlyTaskData].sort((a, b) => parseInt(a.hour) - parseInt(b.hour));

    const labels = sortedHourlyData.map(item => {
      const hour = parseInt(item.hour);
      if (hour === 0) return '12 AM';
      if (hour < 12) return `${hour} AM`;
      if (hour === 12) return '12 PM';
      return `${hour - 12} PM`;
    });

    const data = sortedHourlyData.map(item => item.task_count);
    const backgroundColors = data.map(value => {
      if (value >= 7) return '#EF4444'; // High activity - red
      if (value >= 4) return '#F59E0B'; // Medium activity - yellow
      return '#10B981'; // Low activity - green
    });

    return {
      labels,
      datasets: [
        {
          label: 'Tasks Completed',
          data,
          backgroundColor: backgroundColors,
          borderColor: '#FFFFFF',
          borderWidth: 1,
          borderRadius: 6,
          borderSkipped: false,
        }
      ]
    };
  };

  const getTasksCompletedOverTimeData = () => {
    if (!completionStatusData || completionStatusData.length === 0) {
      // Fallback data if no API data available
      return {
        labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
      datasets: [
        {
            label: 'Tasks Completed',
            data: [28, 35, 42, 38],
            backgroundColor: '#6366F180',
            borderColor: '#6366F1',
            borderWidth: 2,
            borderRadius: 6,
            borderSkipped: false,
          }
        ]
      };
    }

    const labels = completionStatusData.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    const completedData = completionStatusData.map(item => item.completed_count);

    return {
      labels,
      datasets: [
        {
          label: 'Tasks Completed',
          data: completedData,
          backgroundColor: '#6366F180',
          borderColor: '#6366F1',
          borderWidth: 2,
          borderRadius: 6,
          borderSkipped: false,
        }
      ]
    };
  };

  // Calculate real stats from API data
  const getStatsData = () => {
    const defaultStats = {
      totalTasks: 28,
      completedTasks: 20,
      avgTasksPerDay: 4.0,
      completionRate: 71.4
    };

    if (!dashboardData) return defaultStats;

    return {
      totalTasks: dashboardData.weekStats?.totalTasks || defaultStats.totalTasks,
      completedTasks: dashboardData.weekStats?.completedTasks || defaultStats.completedTasks,
      avgTasksPerDay: parseFloat((dashboardData.weekStats?.averageTasksPerDay || defaultStats.avgTasksPerDay).toFixed(2)),
      completionRate: parseFloat((dashboardData.weekStats?.completionRate || defaultStats.completionRate).toFixed(2))
    };
  };

  const stats = getStatsData();
  
  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('darkMode', 'false');
    }
  };

  // Initialize dark mode on component mount
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedDarkMode === 'true' || (savedDarkMode === null && prefersDark)) {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    }
  }, []);
  
  // Add manual test function for debugging
  const testDashboardAPI = async () => {
    console.log('=== MANUAL DASHBOARD API TEST ===');
    try {
      console.log('Testing direct API call...');
      const response = await apiClient.get('/tasks/analytics/dashboard');
      console.log('Direct API response:', response);
      
      // Test if user is authenticated
      const token = localStorage.getItem('access_token');
      console.log('Auth token exists:', !!token);
      console.log('User authenticated:', isAuthenticated);
      
      // Test base API connectivity
      console.log('API Base URL:', process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api');
      
    } catch (error) {
      console.error('Direct API test failed:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
    }
    console.log('=== END API TEST ===');
  };
  
  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }
  
  if (dashboardLoading && notesLoading) {
    return (
      <DashboardLayout 
        user={user} 
        activeItem="analytics" 
        navigationItems={navigationItems}
        onNavigate={handleNavigation}
      >
        <DashboardContainer>
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <Body color="secondary">Loading your dashboard...</Body>
            </div>
          </div>
        </DashboardContainer>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout 
      user={user} 
      activeItem="analytics" 
      navigationItems={navigationItems}
      onNavigate={handleNavigation}
      className="theme-transition"
    >
      <DashboardContainer className="theme-transition">
        {/* Header with AI Insights button */}
        <div className="flex items-center justify-between mb-8 theme-transition">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-900">
              Analytics Dashboard
            </h1>
            <p className="text-gray-600 dark:text-dark-600 mt-1">
              Track your productivity and insights
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant={darkMode ? "accent-gradient" : "outline"}
              onClick={toggleDarkMode}
              leftIcon={<i className={`fa-solid ${darkMode ? 'fa-sun' : 'fa-moon'}`} />}
              className={darkMode ? "shadow-glow" : ""}
            >
              {darkMode ? 'Light Mode' : 'Dark Mode'}
            </Button>
            
            {/* Debug button for testing API */}
            {/* <Button 
              variant="outline" 
              onClick={testDashboardAPI}
              leftIcon={<i className="fa-solid fa-bug" />}
              className="hidden sm:flex bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
            >
              Debug API
            </Button> */}
            
            <Button 
              variant="outline" 
              leftIcon={<i className="fa-solid fa-brain" />}
              className="hidden sm:flex"
            >
              AI Insights
            </Button>
          </div>
        </div>
        
        {/* Stats Cards */}
        <div className="mb-8">
          <StatsGrid cols={4}>
            <StatCard
              label="Total Tasks"
              value={stats.totalTasks}
              change="+12%"
              changeType="positive"
              trend="from last week"
              icon={<i className="fa-solid fa-list-check text-lg" />}
              iconColor="primary"
            />
            
            <StatCard
              label="Completed Tasks"
              value={stats.completedTasks}
              change="+8%"
              changeType="positive"
              trend="from last week"
              icon={<i className="fa-solid fa-circle-check text-lg" />}
              iconColor="success"
            />
            
            <StatCard
              label="Avg. Tasks Per Day"
              value={stats.avgTasksPerDay}
              change="-3%"
              changeType="negative"
              trend="from last week"
              icon={<i className="fa-solid fa-chart-line text-lg" />}
              iconColor="purple"
            />
            
            <StatCard
              label="Completion Rate"
              value={`${stats.completionRate}%`}
              change="+15%"
              changeType="positive"
              trend="from last week"
              icon={<i className="fa-solid fa-percent text-lg" />}
              iconColor="warning"
            />
      </StatsGrid>
        </div>

        {/* Charts Section */}
        <div className="mb-8">
          {/* Productivity Trends - Full Width */}
          <div className="mb-6">
            <ModernLineChart
              title="Productivity Trends"
              data={getProductivityTrendsData()}
              gradient={true}
              smooth={true}
              loading={analyticsLoading}
            />
          </div>
          
          {/* Task Categories and Tasks Completed - Split Width */}
          <ChartsGrid cols={2}>
            <ModernPieChart
              title="Task Categories"
              data={getTaskCategoriesData()}
              showLegend={true}
              loading={dashboardLoading}
            />
            
            <ModernBarChart
              title="Task Activity by Hour"
              data={getTaskActivityByHourData()}
              rounded={true}
              loading={analyticsLoading}
            />
          </ChartsGrid>
        </div>

        {/* Secondary Analytics Row */}
        <div className="mb-8">
          <ChartsGrid cols={3}>
            <PeakPerformanceHours />
            <FocusTimeAnalysis />
            <WeeklyGoalsProgress />
          </ChartsGrid>
        </div>

        {/* Bottom Row */}
        <div className="mb-8">
          <ChartsGrid cols={2}>
            <RecentActivity />
            <AIInsights />
          </ChartsGrid>
        </div>

        {/* Activity Heatmap */}
        <div className="mb-8">
          {heatmapData ? (
            <ActivityHeatmap
              title="Activity Heatmap"
              subtitle="Daily task completion patterns by hour"
              data={heatmapData}
              />
            ) : (
            <Card className="h-96">
              <CardContent className="flex items-center justify-center h-full">
            {analyticsLoading ? (
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <Body color="secondary">Loading activity data...</Body>
                  </div>
                ) : (
                  <Body color="secondary">No activity data available</Body>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default DashboardScreen; 